/**
 * Delete Confirmation Dialog Component
 * Modal dialog for confirming deletion of a revenue entry
 */

import { useTranslation } from 'react-i18next';
import { useUIStore } from '../../store/uiStore';

interface DeleteConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
  title?: string;
  message?: string;
}

export function DeleteConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  isPending,
  title,
  message,
}: DeleteConfirmDialogProps) {
  const { t } = useTranslation();
  const { theme } = useUIStore();
  const isDark = theme === 'dark';

  // Use provided title/message or fall back to translated defaults
  const displayTitle = title || t('modals.deleteConfirm.title');
  const displayMessage = message || t('modals.deleteConfirm.incomeMessage');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="delete-dialog-backdrop"
      />

      {/* Dialog */}
      <div
        className={`relative w-full max-w-sm rounded-2xl shadow-xl ${
          isDark ? 'bg-slate-800' : 'bg-white'
        } animate-scale-in`}
        data-testid="delete-confirm-dialog"
      >
        {/* Icon */}
        <div className="flex justify-center pt-6">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${
            isDark ? 'bg-red-900/30' : 'bg-red-100'
          }`}>
            <svg
              className={`w-7 h-7 ${isDark ? 'text-red-400' : 'text-red-600'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4 text-center">
          <h2 className={`text-lg font-semibold mb-2 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
            {displayTitle}
          </h2>
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            {displayMessage}
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-slate-700/30">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className={`flex-1 py-3 rounded-lg font-medium transition-all ${
              isDark
                ? 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
            } disabled:opacity-50`}
            data-testid="cancel-delete-button"
          >
            {t('modals.deleteConfirm.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            data-testid="confirm-delete-button"
            className={`flex-1 py-3 rounded-lg font-medium transition-all ${
              isPending
                ? 'bg-red-800 text-red-300 cursor-not-allowed'
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}
          >
            {isPending ? t('modals.deleteConfirm.deleting') : t('modals.deleteConfirm.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
